import { clawStarCoins, ClawModel, solveClaw, type ClawRules } from "@nano/puzzle-core";
import { z } from "zod";
import { ARCADE_BOUNCY_KIND, ARCADE_PRIZE_BY_ID } from "./prizes.ts";

export const ARCADE_HOUSE_ID = "arcade" as const;

/** Phones keep a prize at 46 px or more up to this many columns and this tall a pile. */
export const CLAW_LIMITS = { columns: 6, ceiling: 5, prizes: 8, grips: 4 } as const;

export const clawRulesSchema = z
  .object({
    prizes: z
      .array(z.string().refine((id) => ARCADE_PRIZE_BY_ID.has(id), "unknown prize"))
      .min(1)
      .max(CLAW_LIMITS.prizes),
    piles: z
      .array(z.string().regex(/^(?:[a-hA-H]\+?)*$/))
      .min(3)
      .max(CLAW_LIMITS.columns),
    grips: z.array(z.number().int().min(1).max(CLAW_LIMITS.columns)).min(1).max(CLAW_LIMITS.grips),
    ceiling: z.number().int().min(2).max(CLAW_LIMITS.ceiling),
    heavy: z.string().regex(/^[a-h]+$/).optional(),
    bouncy: z.string().regex(/^[a-h]+$/).optional(),
    walls: z.array(z.number().int().min(1).max(CLAW_LIMITS.columns - 1)).min(1).max(2).optional(),
  })
  .strict();

export const arcadeLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(ARCADE_HOUSE_ID),
    contentVersion: z.string().regex(/^arcade-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Fewest coins, proven by the solver when the level was frozen. */
    optimalCoins: z.number().int().min(1),
    threeStarCoins: z.number().int().min(1),
    twoStarCoins: z.number().int().min(1),
    seed: z.number().int().nonnegative().optional(),
    rules: clawRulesSchema,
  })
  .strict();

export type ArcadeLevel = z.infer<typeof arcadeLevelSchema>;

export const arcadePackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(ARCADE_HOUSE_ID),
    contentVersion: z.string().regex(/^arcade-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(arcadeLevelSchema).length(10),
  })
  .strict();

export type ArcadePack = z.infer<typeof arcadePackSchema>;

export function toClawRules(level: ArcadeLevel): ClawRules {
  return level.rules as ClawRules;
}

/** Checks a level against the rules and the solver: problems, or an empty list. */
export function validateArcadeLevel(level: ArcadeLevel, options: { solve?: boolean } = {}): string[] {
  const problems: string[] = [];
  let model: ClawModel;
  try {
    model = new ClawModel(toClawRules(level));
  } catch (error) {
    return [`level ${level.id}: ${(error as Error).message}`];
  }
  const used = new Set(model.prizes.map((prize) => prize.kind));
  if (level.rules.prizes.some((kind) => !used.has(kind))) problems.push(`level ${level.id}: lists a prize it never uses`);
  const wishKinds = model.prizes.filter((prize) => prize.wish).map((prize) => prize.kind);
  // The wish card shows each wished friend once, and none of them also lies in the glass unwished.
  if (new Set(wishKinds).size !== wishKinds.length) problems.push(`level ${level.id}: a wish prize kind appears twice`);
  if (model.prizes.some((prize) => !prize.wish && wishKinds.includes(prize.kind))) problems.push(`level ${level.id}: a wished kind also lies in the glass as a plain prize`);
  if (level.rules.bouncy && [...level.rules.bouncy].some((letter) => level.rules.prizes[letter.charCodeAt(0) - 97] !== ARCADE_BOUNCY_KIND)) {
    problems.push(`level ${level.id}: only the beach ball bounces`);
  }
  const stars = clawStarCoins(level.optimalCoins);
  if (stars.threeStarCoins !== level.threeStarCoins || stars.twoStarCoins !== level.twoStarCoins) {
    problems.push(`level ${level.id}: star thresholds should be ${stars.threeStarCoins} and ${stars.twoStarCoins}`);
  }
  if (options.solve !== false) {
    const solved = solveClaw(model, { maxNodes: 2_000_000 });
    if (solved.status !== "solved") problems.push(`level ${level.id}: solver says ${solved.status}`);
    else if (solved.optimalCoins !== level.optimalCoins) problems.push(`level ${level.id}: optimal is ${solved.optimalCoins}, data says ${level.optimalCoins}`);
  }
  return problems;
}
